import { Injectable } from '@angular/core';
import {
  CanActivate,
  Router,
  UrlTree
} from '@angular/router';

import { jwtDecode } from 'jwt-decode';

import { AuthService } from 'src/app/core/services/AuthService';
import { k } from 'src/environments/properties';

interface PartialTokenPayload {
  exp?: number;
  isPartial2FA?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PartialTokenGuard implements CanActivate {

  constructor(
    private router: Router,
    private authService: AuthService
  ) {}

  canActivate(): boolean | UrlTree {

    /*
     * ==========================================
     * 1. VERIFICAR SESIÓN COMPLETA
     * ==========================================
     *
     * Si ya existe access_token, el usuario
     * ya está autenticado y no debería volver
     * al flujo MFA.
     */

    const accessToken =
      this.authService.getToken('access');

    if (accessToken && this.isTokenValid(accessToken)) {

      return this.router.parseUrl(
        '/dashboard/default'
      );
    }


    /*
     * ==========================================
     * 2. VERIFICAR PARTIAL TOKEN
     * ==========================================
     */

    const partialToken =
      this.authService.getToken('partial');

    if (!partialToken) {

      return this.router.parseUrl(
        '/auth/login'
      );
    }


    /*
     * ==========================================
     * 3. VALIDAR PARTIAL TOKEN
     * ==========================================
     */

    try {

      const payload =
        jwtDecode<PartialTokenPayload>(
          partialToken
        );

      const now =
        Math.floor(Date.now() / 1000);


      /*
       * Debe ser realmente un token MFA parcial.
       */

      if (!payload.isPartial2FA) {

        this.clearPartialToken();

        return this.router.parseUrl(
          '/auth/login'
        );
      }


      /*
       * Debe contener expiración y seguir vigente.
       */

      if (
        !payload.exp ||
        payload.exp <= now
      ) {

        this.clearPartialToken();

        return this.router.parseUrl(
          '/auth/login'
        );
      }


      /*
       * Partial token válido.
       *
       * Permitimos entrar a:
       *
       * /mfa
       * /force-reset-mfa
       */

      return true;

    } catch (error) {

      /*
       * JWT mal formado.
       */

      this.clearPartialToken();

      return this.router.parseUrl(
        '/auth/login'
      );
    }
  }


  /**
   * Comprueba únicamente que el JWT tenga
   * estructura válida y no esté expirado.
   */
  private isTokenValid(
    token: string
  ): boolean {

    try {

      const payload =
        jwtDecode<{ exp?: number }>(
          token
        );

      if (!payload.exp) {
        return false;
      }

      const now =
        Math.floor(Date.now() / 1000);

      return payload.exp > now;

    } catch {

      return false;
    }
  }


  /**
   * Limpia únicamente el token temporal MFA.
   */
  private clearPartialToken(): void {

    localStorage.removeItem(
      k.partial_token
    );
  }
}